"use client";

import { useState } from "react";
import { Camera, Loader2, Sparkles, X } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import UploadFile, {
    FileType,
    type UploadFileType,
} from "@/components/utils/uploadFile";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { REFERENCE_IMAGE } from "../lib/videoRequirements";
import { useAddPhotoLook, useAddPromptLook } from "../api/useAddAvatarLook";
import type { AvatarWithLooks } from "../types";

interface Props {
    avatar: AvatarWithLooks | null;
    onOpenChange: (open: boolean) => void;
}

type LookMode = "photo" | "prompt";

interface PendingRef {
    url?: string;
    assetId?: string;
    previewUrl: string;
    fileName: string;
}

const PHOTO_UPLOAD_PATH = "avatars/heygen/source/photo";
const REF_UPLOAD_PATH = "avatars/heygen/source/prompt-refs";

export default function AddLookModal({ avatar, onOpenChange }: Props) {
    const { locale, dir } = useLocale();
    const [mode, setMode] = useState<LookMode>("photo");
    const [name, setName] = useState("");
    const [assetId, setAssetId] = useState<string | undefined>(undefined);
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [prompt, setPrompt] = useState("");
    const [refs, setRefs] = useState<PendingRef[]>([]);

    const addPhoto = useAddPhotoLook();
    const addPrompt = useAddPromptLook();
    const submitting = addPhoto.isPending || addPrompt.isPending;

    const reset = () => {
        setMode("photo");
        setName("");
        setAssetId(undefined);
        setUrl(undefined);
        setPrompt("");
        setRefs([]);
    };

    const handleClose = (open: boolean) => {
        if (!open) reset();
        onOpenChange(open);
    };

    const canSubmit = (() => {
        if (!avatar || !name.trim()) return false;
        if (mode === "photo") return !!url || !!assetId;
        return prompt.trim().length > 0;
    })();

    const handleSubmit = async () => {
        if (!avatar) return;
        try {
            if (mode === "photo") {
                await addPhoto.mutateAsync({
                    avatarId: avatar.id,
                    name: name.trim(),
                    assetId,
                    url,
                });
            } else {
                await addPrompt.mutateAsync({
                    avatarId: avatar.id,
                    name: name.trim(),
                    prompt: prompt.trim(),
                    referenceImages: refs.map((r) => ({
                        assetId: r.assetId,
                        url: r.url,
                    })),
                });
            }
            handleClose(false);
        } catch {
            // Hooks surface error toasts; keep modal open for retry.
        }
    };

    const open = !!avatar;
    const canAddMoreRefs = refs.length < REFERENCE_IMAGE.maxCount;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl" dir={dir}>
                <DialogHeader>
                    <DialogTitle>
                        {t("avatars.add-look.title", locale, {
                            name: avatar?.name ?? "",
                        })}
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        {t("avatars.add-look.description", locale)}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <Tabs
                        dir={dir}
                        value={mode}
                        onValueChange={(v) => setMode(v as LookMode)}
                    >
                        <TabsList className="grid grid-cols-2">
                            <TabsTrigger value="photo" className="gap-1.5">
                                <Camera className="size-3.5" />
                                {t("avatars.add-look.tab.photo", locale)}
                            </TabsTrigger>
                            <TabsTrigger value="prompt" className="gap-1.5">
                                <Sparkles className="size-3.5" />
                                {t("avatars.add-look.tab.prompt", locale)}
                            </TabsTrigger>
                        </TabsList>

                        <div className="flex flex-col gap-1.5 mt-4">
                            <label className="text-sm font-medium">
                                {t("avatars.add-look.name.label", locale)}
                            </label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={100}
                                placeholder={t(
                                    "avatars.add-look.name.placeholder",
                                    locale
                                )}
                            />
                        </div>

                        <TabsContent
                            value="photo"
                            className="mt-3 flex flex-col gap-3"
                        >
                            <UploadFile
                                options={{
                                    allowedFileTypes: [FileType.Images],
                                    maxFileSize: 32,
                                    fileUploadPath: PHOTO_UPLOAD_PATH,
                                    showImagePreview: true,
                                    className: "h-56 w-full",
                                    title: {
                                        text: t(
                                            "avatars.wizard.step2.photo.upload",
                                            locale
                                        ),
                                        keyName:
                                            "avatars.wizard.step2.photo.upload",
                                    },
                                }}
                                initialFile={
                                    url
                                        ? { fileUrl: url, fileName: "look" }
                                        : null
                                }
                                onUploadFinished={(u: UploadFileType) => {
                                    setUrl(u.fileUrl);
                                    setAssetId(undefined);
                                }}
                            />
                            <p className="text-xs text-muted-foreground">
                                {t("avatars.add-look.photo.hint", locale)}
                            </p>
                        </TabsContent>

                        <TabsContent
                            value="prompt"
                            className="mt-3 flex flex-col gap-3"
                        >
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium">
                                    {t(
                                        "avatars.wizard.step2.prompt.describe",
                                        locale
                                    )}
                                </label>
                                <Textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    maxLength={1000}
                                    rows={4}
                                    placeholder={t(
                                        "avatars.add-look.prompt.placeholder",
                                        locale
                                    )}
                                />
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground">
                                        {t(
                                            "avatars.add-look.prompt.hint",
                                            locale
                                        )}
                                    </span>
                                    <span className="text-xs text-muted-foreground tabular-nums">
                                        {prompt.length}/1000
                                    </span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <div className="flex items-baseline justify-between">
                                    <label className="text-sm font-medium">
                                        {t(
                                            "avatars.wizard.step2.prompt.references",
                                            locale,
                                            { max: REFERENCE_IMAGE.maxCount }
                                        )}
                                    </label>
                                    <span className="text-xs text-muted-foreground">
                                        {refs.length}/{REFERENCE_IMAGE.maxCount}
                                    </span>
                                </div>
                                {canAddMoreRefs && (
                                    <UploadFile
                                        options={{
                                            allowedFileTypes: [FileType.Images],
                                            maxFileSize: 32,
                                            fileUploadPath: REF_UPLOAD_PATH,
                                            className: "h-24 w-full",
                                            title: {
                                                text: t(
                                                    "avatars.wizard.step2.prompt.refs.add",
                                                    locale
                                                ),
                                                keyName:
                                                    "avatars.wizard.step2.prompt.refs.add",
                                            },
                                            subtitle: {
                                                text: t(
                                                    "avatars.wizard.step2.prompt.refs.subtitle",
                                                    locale,
                                                    {
                                                        max: REFERENCE_IMAGE.maxCount,
                                                    }
                                                ),
                                                keyName:
                                                    "avatars.wizard.step2.prompt.refs.subtitle",
                                            },
                                        }}
                                        onUploadFinished={(
                                            u: UploadFileType
                                        ) => {
                                            setRefs((r) => [
                                                ...r,
                                                {
                                                    url: u.fileUrl,
                                                    previewUrl: u.fileUrl,
                                                    fileName: u.fileName,
                                                },
                                            ]);
                                        }}
                                    />
                                )}
                                {refs.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {refs.map((img, idx) => (
                                            <div
                                                key={idx}
                                                className="group relative h-20 w-20 rounded-md overflow-hidden border bg-muted"
                                            >
                                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                                <img
                                                    src={img.previewUrl}
                                                    alt={img.fileName}
                                                    className="w-full h-full object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setRefs((r) =>
                                                            r.filter(
                                                                (_, i) =>
                                                                    i !== idx
                                                            )
                                                        )
                                                    }
                                                    aria-label={t(
                                                        "avatars.wizard.step2.prompt.refs.remove",
                                                        locale
                                                    )}
                                                    title={t(
                                                        "avatars.wizard.step2.prompt.refs.remove",
                                                        locale
                                                    )}
                                                    className="absolute top-1 end-1 inline-flex items-center justify-center size-5 rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-black/75 backdrop-blur-sm"
                                                >
                                                    <X className="size-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>

                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => handleClose(false)}
                        disabled={submitting}
                    >
                        {t("avatars.delete-confirm.cancel", locale)}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit || submitting}
                    >
                        {submitting && (
                            <Loader2 className="size-4 me-2 animate-spin" />
                        )}
                        {t("avatars.add-look.submit", locale)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
